<template>
  <div class="dashboard container-fluid">
    <!-- Title -->
    <h2 class="title mt-3 mb-4">AE/SAE Risk Monitoring Dashboard</h2>

    <!-- Top row -->
    <div class="row gx-3 gy-3">
      <!-- Summary card -->
      <div class="col-md-4">
        <div class="card summary-card h-100 p-3">
          <!-- Total Open AEs -->
          <small class="text-muted">Total Open AEs</small>
          <div class="d-flex align-items-baseline mb-2">
            <h1 class="value me-2 mb-0">{{ totalOpenAEs }}</h1>
            <span :class="openAEPct > 0 ? 'text-success' : 'text-danger'" class="fs-5">
              {{ openAEPct }}%
            </span>
          </div>
          <div class="progress-bar-container mb-3">
            <div class="progress-bar-fill bg-success" :style="{ width: openAEPct + '%' }"></div>
          </div>

          <!-- SAE Rate -->
          <small class="text-muted">SAE Rate</small>
          <div class="d-flex align-items-baseline mb-2">
            <h2 class="value mb-0">{{ saeRate }}%</h2>
          </div>
          <div class="progress-bar-container mb-3">
            <div class="progress-bar-fill bg-info" :style="{ width: saeRate + '%' }"></div>
          </div>

          <!-- At-Risk Sites -->
          <small class="text-muted">At-Risk Sites</small>
          <div class="d-flex align-items-baseline mb-2">
            <h2 class="value mb-0">{{ atRiskPct }}%</h2>
          </div>
          <div class="progress-bar-container mb-3">
            <div class="progress-bar-fill bg-danger" :style="{ width: atRiskPct + '%' }"></div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="col-md-8">
        <div class="row chart-card gx-0 gy-0">
          <!-- Donut -->
          <div class="col-md-6 p-3" ref="donutWrapper">
            <div class="chart-header mb-2"><strong>Grade-wise (MHSTAT)</strong></div>
            <apexchart ref="mhstatChart" type="donut" width="100%" height="240" :options="donutOptions"
              :series="donutSeries" />
          </div>

          <!-- Line -->
          <div class="col-md-6 p-3">
            <div class="chart-header mb-2"><strong>SAE Trend Over Time</strong></div>
            <apexchart type="line" width="100%" height="240" :options="lineOptions" :series="lineSeries" />
          </div>
        </div>
      </div>
    </div>

    <!-- Site Performance Tables -->
    <div class="row gx-3 gy-3 mt-4">
      <!-- Left: filter control & detail table -->
      <div class="col-md-4">
        <div class="card table-card p-3 minheightcard">
          <div class="tabs mb-3">
            <button @click="selectedTab = 'all'" :class="{ selected: selectedTab === 'all' }">All Sites</button>
            <button @click="selectedTab = 'at-risk'" :class="{ selected: selectedTab === 'at-risk' }">At-Risk
              Sites</button>
          </div>
          <div class="table-responsive detail-table">
            <table class="table mb-0">
              <thead class="table-light">
                <tr>
                  <th>Site ID</th>
                  <th>AE Category</th>
                  <th>AE Frequency</th>
                  <th>AE Term</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in filteredRows" :key="row.USUBJID + row.AETERM">
                  <td>{{ row.SITEID }}</td>
                  <td>{{ row['AE CATEGORY'] || '—' }}</td>
                  <td>{{ row['AE FREQUENCY'] || '—' }}</td>
                  <td>{{ row.AETERM || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Right: summary table -->
      <div class="col-md-8">
        <div class="card table-card p-3 minheightcard">
          <h5 class="mb-3">Site Summary</h5>
          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-light">
                <tr>
                  <th>Site</th>
                  <th>AEs</th>
                  <th>SAEs</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in siteData" :key="s.name">
                  <td>{{ s.name }}</td>
                  <td>{{ s.AEs }}</td>
                  <td>{{ s.SAEs }}</td>
                  <td>
                    <span :class="['status-pill', s.statusClass]">
                      {{ s.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Safety Signal & Legend (unchanged) -->
    <div class="card signal-card mt-4 p-3 d-flex justify-content-between align-items-center">
      <div>
        <span class="me-2">⚠️</span>
        <strong>SAFETY SIGNAL DETECTED:</strong>
        Headache events increased 220% vs. historical baseline
      </div>
      <div>
        <button class="btn btn-sm btn-outline-primary me-2" @click="onInvestigate">
          Investigate
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="onDismiss">
          Dismiss
        </button>
      </div>
    </div>

    <div class="legend d-flex justify-content-center gap-4 mt-4 mb-3">
      <div><span class="dot new"></span> New</div>
      <div><span class="dot under-review"></span> Under Review</div>
      <div><span class="dot closed"></span> Closed</div>
    </div>
  </div>
</template>

<script src="./ClinicalSafetyDashboard.js">

</script>

<style scoped>
.dashboard {
  font-family: sans-serif;
}

.title {
  font-size: 1.8rem;
}

/* Progress bars */
.progress-bar-container {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 3px;
}

/* Tabs */
.tabs {
  display: flex;
  margin-bottom: 1rem;
}

.tabs button {
  flex: 1;
  padding: .375rem .75rem;
  font-size: .875rem;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  border-radius: .375rem;
  cursor: pointer;
}

.tabs button.selected {
  background: #fff;
  color: #111827;
  border-color: #d1d5db;
}

/* Tables */
.table-responsive {
  max-height: 400px;
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  background: #f8f9fa;
  font-weight: 600;
  z-index: 2;
}

.minheightcard {
  height: 300px;
  overflow: auto;
}

/* Status pills */
.status-pill {
  font-size: .75rem;
  padding: 2px 6px;
  border-radius: 4px;
  color: #fff;
}

.status-pill.critical {
  background: #f46a6a;
}

.status-pill.warning {
  background: #f1b44c;
}

.status-pill.good {
  background: #34c38f;
}

/* Chart cards */
.chart-card {
  background: #fff;
  border-radius: .5rem;
}

.chart-header {
  font-size: 1rem;
  margin-bottom: .5rem;
}

/* Signal + legend (keep existing styles) */
.signal-card {
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: .5rem;
}

.legend .new {
  background: #34c38f;
}

.legend .under-review {
  background: #c8c9ce;
}

.legend .closed {
  background: #f1b44c;
}

.legend .dot {
  margin-right: 4px;
}
</style>
